import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";
import PropTypes from "prop-types";

import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import Tooltip from "Components/Common/Tooltip";

import getFileExtension from "utils/getFileExtension";
import removeExtension from "utils/removeExtension";
import downloadFile from "utils/downloadFile";
import { getImageUrl } from "utils";

import { Folder, FolderName } from "./style";

import folderImage from "assets/images/folder.png";
import pdfImg from "assets/images/pdf.png";
import docxImg from "assets/images/docx.png";

const imgTypes = ["jpg", "png", "jpeg"];

const docTypes = ["doc", "docx"];

const getImageProps = (documentType = "", path) => {
  switch (documentType.toLowerCase()) {
    case "pdf":
      return { src: pdfImg, height: "75px", style: { marginTop: "5px" } };

    case "doc":
    case "docx":
      return {
        src: docxImg,
        height: "75px",
        width: "65px",
        style: { marginTop: "5px" },
      };

    case "jpg":
    case "png":
    case "jpeg":
      return {
        src: getImageUrl(path),
        height: "70px",
        style: { marginTop: "5px" },
      };

    default:
      return { src: folderImage };
  }
};

const DocumentItem = ({
  document: { path = "", userName, documentName: documentTypeName, folderName },
  setQuery = () => {},
  setSearchedText = () => {},
}) => {
  const [documentToPreview, setDocumentToPreview] = useState(null);

  const fileType = useMemo(
    () => getFileExtension(path).toLowerCase().trim(),
    [path]
  );

  const isFolder = useMemo(
    () =>
      !(
        fileType === "pdf" ||
        imgTypes.includes(fileType) ||
        docTypes.includes(fileType)
      ),
    [fileType]
  );

  const fileClickHandler = async (documentTypeName, documentURL) => {
    const fileExtension = getFileExtension(documentTypeName ?? "");
    const url = getImageUrl(documentURL ?? "");
    if (isFolder) {
      setQuery((prev) => ({ ...prev, path, search: "" }));
      setSearchedText("");
    } else if (docTypes.includes(fileExtension)) {
      downloadFile(url, `${documentTypeName}.${fileExtension}`);
      toast.success("Document downloaded successfully");
    } else {
      setDocumentToPreview({
        documentTypeName,
        url,
        documentType: fileExtension,
        documentName: removeExtension(documentTypeName ?? ""),
      });
    }
  };

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}
      <Folder
        isImage={imgTypes.includes(fileType)}
        onDoubleClick={() => fileClickHandler(documentTypeName, path)}
      >
        <img {...getImageProps(fileType, path)} />
        <Tooltip title={folderName ?? userName ?? documentTypeName}>
          <FolderName fontSize={13} variant="body2">
            {folderName ?? userName ?? documentTypeName}
          </FolderName>
        </Tooltip>
      </Folder>
    </>
  );
};

DocumentItem.propTypes = {
  document: PropTypes.object,
  setQuery: PropTypes.func,
  setSearchedText: PropTypes.func,
};

export default DocumentItem;
